import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { Box, LinearProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import Badge from 'react-bootstrap/Badge';
import { showNotification } from 'src/shared/components/Notification';
import {
  ADDRESS_TYPE_ANOTHER_HUB,
  ADDRESS_TYPE_HUB_CUSTOMER,
  ADDRESS_TYPE_MERCHANT,
  ADDRESS_TYPE_MERCHANT_CUSTOMER,
  ADDRESS_TYPE_OWN_HUB,
  ERROR_MESSAGES,
} from 'src/utils/pnd/PndConstants';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import PageHeader from 'src/shared/components/layout/PageHeader';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const RiderCurrentlyProcessingOrderListInex = () => {
  const { id } = useParams();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reactTableData, setReactTableData] = useState({ tableHeaderData: [], tableRowsData: [] });
  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);

  useEffect(() => {
    if (id) {
      viewOrderDetails();
    }
  }, [id]);

  const viewOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await PndRestApi.GetRiderWiseActiveOrderList(id);
      if (response.status === 200 && response.data.status === 200) {
        const orderList = response.data.data || [];
        setReactTableData({ tableHeaderData: orderColumns, tableRowsData: orderList });
        if (orderList.length > 0) {
          setSelectedOrder(orderList[0]);
        }
      } else if (response.status === 200 && response.data.status === 204) {
        setNoRecordFound(true);
      } else {
        showNotification(
          'theme-light',
          'ltr',
          `SuccessCode: ${response.data.status}`,
          `Message:'No orders were found for the selected Rider'`,
        );
      }
    } catch (error) {
      console.log('error', error);
      showNotification(
        'theme-light',
        'ltr',
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const orderColumns = useMemo(() => [
    { key: 'orderNumber', header: 'Tracking No', sortable: true },
    { key: 'erpOrderNo', header: 'ERP No', sortable: true },
    {
      key: 'orderType',
      header: 'Order Type',
      sortable: true,
      render: (_, row) => (
        <>
          {row.orderType === 1 ? (
            <Badge bg="success">DROP OFF</Badge>
          ) : row.orderType === 2 ? (
            <Badge bg="primary">PICK UP</Badge>
          ) : (
            <Badge bg="warning text-dark">PnD</Badge>
          )}
        </>
      ),
    },
    { key: 'orderStatus', header: 'Status', sortable: true },
  ], []);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <PageHeader
          icon={<ReceiptLongIcon fontSize="large" />}
          title="Rider Wise Order Details"
          subtitle="View active orders assigned to the rider in a detailed master-detail view."
          gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
        />
        <Row style={{ marginTop: '20px' }}>
          {/* Left Panel: Master List with essential details */}
          <Col md={7} lg={7}>
            <RadioSelectTable
              rows={reactTableData?.tableRowsData || []}
              columns={orderColumns}
              rowKey="orderNumber"
              selectedKey={selectedOrder?.orderNumber}
              onSelect={setSelectedOrder}
              searchKeys={['orderNumber', 'orderStatus', 'storeName']}
              searchPlaceholder="Search orders..."
              pageSizes={[5, 10, 20]}
              headerTitle="Active Orders"
            />
          </Col>

          {/* Right Panel: Detailed View Card */}
          <Col md={5} lg={5}>
            {selectedOrder ? (
              <Card style={{
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                minHeight: '520px',
                background: '#ffffff',
                overflow: 'hidden',
              }}>
                {/* Header Banner */}
                <div style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', padding: '24px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>Order No. {selectedOrder.orderNumber}</h4>
                      <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>Store: {selectedOrder.storeName || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="badge bg-success" style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                        {selectedOrder.orderStatus}
                      </span>
                      {selectedOrder.orderType === 1 ? (
                        <span className="badge bg-light text-dark" style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                          DROP OFF
                        </span>
                      ) : selectedOrder.orderType === 2 ? (
                        <span className="badge bg-light text-dark" style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                          PICK UP
                        </span>
                      ) : (
                        <span className="badge bg-light text-dark" style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                          PnD
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Order Profile */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Order Profile
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Rider Name:</span>
                          <span className="fw-bold small text-dark">{selectedOrder.riderName || '—'}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Store Name:</span>
                          <span className="fw-bold small text-dark">{selectedOrder.storeName || '—'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Route Details */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Route Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">From Type:</span>
                          <span className="fw-bold small text-dark">
                            {selectedOrder.fromTypeId === ADDRESS_TYPE_OWN_HUB ? 'Own Hub' :
                             selectedOrder.fromTypeId === ADDRESS_TYPE_HUB_CUSTOMER ? 'Hub Customer' :
                             selectedOrder.fromTypeId === ADDRESS_TYPE_MERCHANT ? 'Merchant' :
                             selectedOrder.fromTypeId === ADDRESS_TYPE_ANOTHER_HUB ? 'Another Hub' :
                             selectedOrder.fromTypeId === ADDRESS_TYPE_MERCHANT_CUSTOMER ? 'Merchant Customer' : 'Unknown'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">To Type:</span>
                          <span className="fw-bold small text-dark">
                            {selectedOrder.toTypeId === ADDRESS_TYPE_OWN_HUB ? 'Own Hub' :
                             selectedOrder.toTypeId === ADDRESS_TYPE_HUB_CUSTOMER ? 'Hub Customer' :
                             selectedOrder.toTypeId === ADDRESS_TYPE_MERCHANT ? 'Merchant' :
                             selectedOrder.toTypeId === ADDRESS_TYPE_ANOTHER_HUB ? 'Another Hub' :
                             selectedOrder.toTypeId === ADDRESS_TYPE_MERCHANT_CUSTOMER ? 'Merchant Customer' : 'Unknown'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Address Details */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Address Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <span className="text-muted small d-block mb-1">Pickup Address:</span>
                          <span className="fw-bold small text-dark" style={{ wordBreak: 'break-word' }}>{selectedOrder.pickupAddress || '—'}</span>
                        </div>
                        <div style={{ marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                          <span className="text-muted small d-block mb-1">Drop Off Address:</span>
                          <span className="fw-bold small text-dark" style={{ wordBreak: 'break-word' }}>{selectedOrder.dropOffAddress || '—'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center', minHeight: '520px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <p className="text-muted mb-0">Select an order from the list to view its detailed information.</p>
              </Card>
            )}
          </Col>
        </Row>
        {isNoRecordFound && (
          <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center', marginTop: '20px' }}>
            <p className="text-muted mb-0">No active orders found for the selected Rider.</p>
          </Card>
        )}
        {loading && <LinearProgress color="primary" />}
      </Container>
    </Box>
  );
};

export default RiderCurrentlyProcessingOrderListInex;